import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { SortableHeader, sortData } from '../components/SortableHeader';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Sparkles, Wallet } from 'lucide-react';
import { 
    Users as UsersIcon, 
    UserPlus, 
    Trash2,
    Mail,
    Lock,
    User,
    Loader2,
    Shield,
    Eye,
    MoreVertical,
    UserCog,
    ArrowUpCircle,
    ArrowDownCircle,
    Pencil,
    UserCheck,
    KeyRound,
    Search,
    Filter,
    X,
    CheckCircle,
    Clock,
    Link2
} from 'lucide-react';

const API = `/api`;

export default function Users() {
    const { user, getAuthHeaders } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    const [searchTerm, setSearchTerm] = useState('');
    const [roleFilter, setRoleFilter] = useState('all');
    
    // Sorting states
    const [sortKey, setSortKey] = useState('name');
    const [sortDirection, setSortDirection] = useState('asc');
    
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'usuario'
    });

    // Edit name state
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [newName, setNewName] = useState('');

    // Assign to supervisor2 state
    const [isAssignDialogOpen, setIsAssignDialogOpen] = useState(false);
    const [assigningUser, setAssigningUser] = useState(null);
    const [supervisors2, setSupervisors2] = useState([]);
    const [selectedSupervisor2, setSelectedSupervisor2] = useState('');

    // Change password state
    const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
    const [passwordUser, setPasswordUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Share link state
    const [isShareLinkDialogOpen, setIsShareLinkDialogOpen] = useState(false);
    const [shareLinkUser, setShareLinkUser] = useState(null);
    const [shareLinkValue, setShareLinkValue] = useState('');
    
    // Handle sorting
    const handleSort = (key, direction) => {
        setSortKey(key);
        setSortDirection(direction);
    };
    
    // Sort users
    const sortedUsers = useMemo(() => {
        return sortData(users, sortKey, sortDirection);
    }, [users, sortKey, sortDirection]);

    // Filter users by search term and role
    const filteredUsers = useMemo(() => {
        return sortedUsers.filter(u => {
            const matchesSearch = searchTerm === '' || 
                u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                u.email.toLowerCase().includes(searchTerm.toLowerCase());
            const matchesRole = roleFilter === 'all' || u.role === roleFilter;
            return matchesSearch && matchesRole;
        });
    }, [sortedUsers, searchTerm, roleFilter]);

    const clearFilters = () => {
        setSearchTerm('');
        setRoleFilter('all');
    };

    const hasActiveFilters = searchTerm !== '' || roleFilter !== 'all';

    useEffect(() => {
        fetchUsers();
        if (user?.role === 'admin' || user?.role === 'supervisor') {
            fetchSupervisors2();
        }
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/users`, {
                headers: getAuthHeaders()
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
            toast.error('Error al cargar los usuarios');
        } finally {
            setLoading(false);
        }
    };

    const fetchSupervisors2 = async () => {
        try {
            const response = await axios.get(`${API}/users/supervisors2`, {
                headers: getAuthHeaders()
            });
            setSupervisors2(response.data);
        } catch (error) {
            console.error('Error fetching supervisors2:', error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleRoleChange = (value) => {
        setFormData(prev => ({ ...prev, role: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        
        try {
            const response = await axios.post(`${API}/users`, formData, {
                headers: getAuthHeaders()
            });
            setUsers([...users, response.data]);
            setFormData({ name: '', email: '', password: '', role: 'usuario' });
            setIsDialogOpen(false);
            toast.success('Usuario creado correctamente');
        } catch (error) {
            console.error('Error creating user:', error);
            toast.error(error.response?.data?.detail || 'Error al crear el usuario');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (userId) => {
        if (!window.confirm('¿Estás seguro de eliminar este usuario?')) return;
        
        try {
            await axios.delete(`${API}/users/${userId}`, {
                headers: getAuthHeaders()
            });
            setUsers(users.filter(u => u.id !== userId));
            toast.success('Usuario eliminado correctamente');
        } catch (error) {
            console.error('Error deleting user:', error);
            toast.error(error.response?.data?.detail || 'Error al eliminar el usuario');
        }
    };

    const handleToggleExtra = async (userId, isExtra) => {
        try {
            const res = await axios.patch(`${API}/users/${userId}/extra`, { is_extra: isExtra }, { headers: getAuthHeaders() });
            setUsers(users.map(x => x.id === userId ? { ...x, is_extra: isExtra } : x));
            toast.success(res.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar usuario extra');
        }
    };

    const handleTogglePayPal = async (userId, isPayPal) => {
        try {
            const res = await axios.patch(`${API}/users/${userId}/paypal`, { is_paypal: isPayPal }, { headers: getAuthHeaders() });
            setUsers(users.map(x => x.id === userId ? { ...x, is_paypal: isPayPal } : x));
            toast.success(res.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar usuario PayPal');
        }
    };

    const handleChangeRole = async (userId, newRole) => {
        try {
            const response = await axios.patch(
                `${API}/users/${userId}/role`,
                { role: newRole },
                { headers: getAuthHeaders() }
            );
            
            setUsers(users.map(u => 
                u.id === userId ? { ...u, role: response.data.role } : u
            ));
            
            toast.success(`Rol actualizado a ${getRoleLabel(newRole)}`);
        } catch (error) {
            console.error('Error changing role:', error);
            toast.error(error.response?.data?.detail || 'Error al cambiar el rol');
        }
    };

    const openEditDialog = (userToEdit) => {
        setEditingUser(userToEdit);
        setNewName(userToEdit.name);
        setIsEditDialogOpen(true);
    };

    const handleUpdateName = async () => {
        if (!newName.trim() || newName.trim().length < 2) {
            toast.error('El nombre debe tener al menos 2 caracteres');
            return;
        }

        try {
            const response = await axios.patch(
                `${API}/users/${editingUser.id}/name`,
                { name: newName.trim() },
                { headers: getAuthHeaders() }
            );
            
            setUsers(users.map(u => 
                u.id === editingUser.id ? { ...u, name: response.data.name } : u
            ));
            
            setIsEditDialogOpen(false);
            setEditingUser(null);
            setNewName('');
            toast.success('Nombre actualizado correctamente');
        } catch (error) {
            console.error('Error updating name:', error);
            toast.error(error.response?.data?.detail || 'Error al actualizar el nombre');
        }
    };

    const openAssignDialog = (userToAssign) => {
        setAssigningUser(userToAssign);
        setSelectedSupervisor2(userToAssign.assigned_to || '');
        setIsAssignDialogOpen(true);
    };

    const handleAssignToSupervisor2 = async () => {
        try {
            const response = await axios.patch(
                `${API}/users/${assigningUser.id}/assign`,
                { supervisor2_id: selectedSupervisor2 || null },
                { headers: getAuthHeaders() }
            );
            
            setUsers(users.map(u => 
                u.id === assigningUser.id 
                    ? { ...u, assigned_to: response.data.assigned_to, assigned_to_name: response.data.assigned_to_name } 
                    : u
            ));
            
            setIsAssignDialogOpen(false);
            setAssigningUser(null);
            setSelectedSupervisor2('');
            toast.success(response.data.message);
        } catch (error) {
            console.error('Error assigning user:', error);
            toast.error(error.response?.data?.detail || 'Error al asignar usuario');
        }
    };

    const openPasswordDialog = (userToEdit) => {
        setPasswordUser(userToEdit);
        setNewPassword('');
        setConfirmPassword('');
        setIsPasswordDialogOpen(true);
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        setIsSubmitting(true);
        try {
            await axios.patch(
                `${API}/users/${passwordUser.id}/password`,
                { new_password: newPassword },
                { headers: getAuthHeaders() }
            );
            
            setIsPasswordDialogOpen(false);
            setPasswordUser(null);
            setNewPassword('');
            setConfirmPassword('');
            toast.success('Contraseña actualizada correctamente');
        } catch (error) {
            console.error('Error changing password:', error);
            toast.error(error.response?.data?.detail || 'Error al cambiar la contraseña');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Share link functions
    const openShareLinkDialog = (userToEdit) => {
        setShareLinkUser(userToEdit);
        setShareLinkValue(userToEdit.share_link || '');
        setIsShareLinkDialogOpen(true);
    };

    const handleSaveShareLink = async () => {
        setIsSubmitting(true);
        try {
            await axios.patch(
                `${API}/users/${shareLinkUser.id}/share-link`,
                { share_link: shareLinkValue },
                { headers: getAuthHeaders() }
            );
            setUsers(prev => prev.map(u =>
                u.id === shareLinkUser.id ? { ...u, share_link: shareLinkValue } : u
            ));
            setIsShareLinkDialogOpen(false);
            toast.success('Enlace actualizado');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al guardar enlace');
        } finally {
            setIsSubmitting(false);
        }
    };

    const getRoleColor = (role) => {
        switch (role) {
            case 'admin': return { bg: 'bg-sky-100', text: 'text-sky-700', border: 'border-sky-200' };
            case 'supervisor': return { bg: 'bg-purple-100', text: 'text-purple-700', border: 'border-purple-200' };
            case 'supervisor2': return { bg: 'bg-amber-100', text: 'text-amber-700', border: 'border-amber-200' };
            default: return { bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
        }
    };

    const getRoleBadgeClass = (role) => {
        switch (role) {
            case 'admin': return 'badge-admin';
            case 'supervisor': return 'badge-supervisor';
            case 'supervisor2': return 'badge-supervisor2';
            default: return 'badge-usuario';
        }
    };

    const getRoleLabel = (role) => {
        switch (role) {
            case 'admin': return 'Administrador';
            case 'supervisor': return 'Supervisor';
            case 'supervisor2': return 'Supervisor 2';
            default: return 'Usuario';
        }
    };

    const getRoleIcon = (role) => {
        switch (role) {
            case 'admin': return Shield;
            case 'supervisor': return Eye;
            case 'supervisor2': return UserCog;
            default: return User;
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="users-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">
                        Gestión de Usuarios
                    </h1>
                    <p className="text-slate-500 mt-1">
                        Administra los usuarios del sistema
                    </p>
                </div>
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button className="bg-sky-500 hover:bg-sky-600" data-testid="add-user-btn">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Nuevo Usuario
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                        <DialogHeader>
                            <DialogTitle className="text-xl font-semibold text-slate-800">
                                Crear Nuevo Usuario
                            </DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-5 mt-4">
                            <div className="space-y-2">
                                <Label htmlFor="name" className="text-slate-700 font-medium">
                                    Nombre Completo
                                </Label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="name"
                                        name="name"
                                        placeholder="Juan Pérez"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className="pl-10 h-11"
                                        required
                                        data-testid="user-name-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-slate-700 font-medium">
                                    Correo Electrónico
                                </Label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="email"
                                        name="email"
                                        type="email"
                                        placeholder="correo@ejemplo.com"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className="pl-10 h-11"
                                        required
                                        data-testid="user-email-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="password" className="text-slate-700 font-medium">
                                    Contraseña
                                </Label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                    <Input
                                        id="password"
                                        name="password"
                                        type="password"
                                        placeholder="Contraseña segura"
                                        value={formData.password}
                                        onChange={handleInputChange}
                                        className="pl-10 h-11"
                                        required
                                        data-testid="user-password-input"
                                    />
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label className="text-slate-700 font-medium">
                                    Rol
                                </Label>
                                <Select value={formData.role} onValueChange={handleRoleChange}>
                                    <SelectTrigger className="h-11" data-testid="user-role-select">
                                        <SelectValue placeholder="Selecciona un rol" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="usuario">Usuario</SelectItem>
                                        {(user?.role === 'admin' || user?.role === 'supervisor') && (
                                            <SelectItem value="supervisor2">Supervisor 2</SelectItem>
                                        )}
                                        {user?.role === 'admin' && (
                                            <>
                                                <SelectItem value="supervisor">Supervisor</SelectItem>
                                                <SelectItem value="admin">Administrador</SelectItem>
                                            </>
                                        )}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="flex gap-4 pt-4">
                                <Button
                                    type="button"
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setIsDialogOpen(false)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    type="submit"
                                    className="flex-1 bg-sky-500 hover:bg-sky-600"
                                    disabled={isSubmitting}
                                    data-testid="submit-user-btn"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Creando...
                                        </>
                                    ) : (
                                        'Crear Usuario'
                                    )}
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                    { label: 'Administradores', count: users.filter(u => u.role === 'admin').length, icon: Shield, bgColor: 'bg-sky-50', iconColor: 'text-sky-500', role: 'admin' },
                    { label: 'Supervisores', count: users.filter(u => u.role === 'supervisor').length, icon: Eye, bgColor: 'bg-purple-50', iconColor: 'text-purple-500', role: 'supervisor' },
                    { label: 'Supervisores 2', count: users.filter(u => u.role === 'supervisor2').length, icon: UserCog, bgColor: 'bg-amber-50', iconColor: 'text-amber-500', role: 'supervisor2' },
                    { label: 'Usuarios', count: users.filter(u => u.role === 'usuario').length, icon: User, bgColor: 'bg-slate-50', iconColor: 'text-slate-500', role: 'usuario' }
                ].map((stat, index) => (
                    <Card 
                        key={stat.label} 
                        className={`border-0 shadow-sm cursor-pointer transition-all duration-200 hover:shadow-md hover:scale-[1.02] ${roleFilter === stat.role ? 'ring-2 ring-sky-400' : ''}`}
                        onClick={() => setRoleFilter(roleFilter === stat.role ? 'all' : stat.role)}
                        style={{ animationDelay: `${index * 0.1}s` }}
                        data-testid={`stat-${stat.role}`}
                    >
                        <CardContent className="p-4 flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                                <stat.icon className={`w-6 h-6 ${stat.iconColor}`} />
                            </div>
                            <div>
                                <p className="text-2xl font-bold text-slate-800">{stat.count}</p>
                                <p className="text-sm text-slate-500">{stat.label}</p>
                            </div>
                            {roleFilter === stat.role && (
                                <CheckCircle className="w-5 h-5 text-sky-500 ml-auto" />
                            )}
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Search and Filters */}
            <Card className="border-0 shadow-sm">
                <CardContent className="p-4">
                    <div className="flex flex-wrap items-center gap-4">
                        {/* Search Input */}
                        <div className="relative flex-1 min-w-[200px]">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <Input
                                placeholder="Buscar por nombre o correo..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 h-10"
                                data-testid="user-search-input"
                            />
                        </div>
                        
                        {/* Role Filter */}
                        <Select value={roleFilter} onValueChange={setRoleFilter}>
                            <SelectTrigger className="w-[180px] h-10" data-testid="role-filter-select">
                                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                                <SelectValue placeholder="Filtrar por rol" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los roles</SelectItem>
                                <SelectItem value="admin">Administrador</SelectItem>
                                <SelectItem value="supervisor">Supervisor</SelectItem>
                                <SelectItem value="supervisor2">Supervisor 2</SelectItem>
                                <SelectItem value="usuario">Usuario</SelectItem>
                            </SelectContent>
                        </Select>

                        {/* Clear Filters */}
                        {hasActiveFilters && (
                            <Button
                                variant="ghost"
                                onClick={clearFilters}
                                className="text-slate-500 hover:text-slate-700"
                                data-testid="clear-filters-btn"
                            >
                                <X className="w-4 h-4 mr-1" />
                                Limpiar
                            </Button>
                        )}

                        {/* Results Count */}
                        <span className="text-sm text-slate-500 ml-auto">
                            {filteredUsers.length} de {users.length} usuarios
                        </span>
                    </div>
                </CardContent>
            </Card>

            {/* Users Table */}
            <Card className="border-0 shadow-sm">
                <CardHeader className="pb-4">
                    <CardTitle className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        <UsersIcon className="w-5 h-5 text-sky-500" />
                        Lista de Usuarios
                        {hasActiveFilters && (
                            <span className="text-sm font-normal text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full">
                                Filtrado
                            </span>
                        )}
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    {filteredUsers.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="data-table table-zebra">
                                <thead>
                                    <tr>
                                        <SortableHeader 
                                            label="Usuario" 
                                            sortKey="name" 
                                            currentSort={sortKey} 
                                            currentDirection={sortDirection} 
                                            onSort={handleSort} 
                                        />
                                        <th className="hidden md:table-cell">
                                            <SortableHeader 
                                                label="Correo" 
                                                sortKey="email" 
                                                currentSort={sortKey} 
                                                currentDirection={sortDirection} 
                                                onSort={handleSort} 
                                            />
                                        </th>
                                        <SortableHeader 
                                            label="Rol" 
                                            sortKey="role" 
                                            currentSort={sortKey} 
                                            currentDirection={sortDirection} 
                                            onSort={handleSort} 
                                        />
                                        {(user?.role === 'admin' || user?.role === 'supervisor') && (
                                            <SortableHeader 
                                                label="Asignado a" 
                                                sortKey="assigned_to_name" 
                                                currentSort={sortKey} 
                                                currentDirection={sortDirection} 
                                                onSort={handleSort} 
                                            />
                                        )}
                                        <SortableHeader 
                                            label="Fecha de Creación" 
                                            sortKey="created_at" 
                                            currentSort={sortKey} 
                                            currentDirection={sortDirection} 
                                            onSort={handleSort} 
                                        />
                                        {(user?.role === 'admin' || user?.role === 'supervisor') && <th className="w-20">Acciones</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredUsers.map((u) => {
                                        const RoleIcon = getRoleIcon(u.role);
                                        const roleColor = getRoleColor(u.role);
                                        const canEdit = (user?.role === 'admin' || user?.role === 'supervisor') && 
                                            (u.role !== 'admin' || user?.role === 'admin');
                                        // Admin can manage all users except other admins
                                        // Supervisor can manage usuarios and supervisor2, but not other supervisors or admins
                                        const canManageRole = (user?.role === 'admin' && u.id !== user.id && u.role !== 'admin') ||
                                            (user?.role === 'supervisor' && u.id !== user.id && (u.role === 'usuario' || u.role === 'supervisor2'));
                                        const canDelete = (user?.role === 'admin' && u.id !== user.id && u.role !== 'admin') ||
                                            (user?.role === 'supervisor' && u.id !== user.id && (u.role === 'usuario' || u.role === 'supervisor2'));
                                        
                                        return (
                                            <tr key={u.id} className="hover:bg-slate-50 transition-colors" data-testid={`user-row-${u.id}`}>
                                                <td>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-10 h-10 rounded-full ${roleColor.bg} flex items-center justify-center border ${roleColor.border}`}>
                                                            <span className={`${roleColor.text} font-semibold`}>
                                                                {u.name.charAt(0).toUpperCase()}
                                                            </span>
                                                        </div>
                                                        <div>
                                                            <span className="font-medium text-slate-700 block">
                                                                {u.name}
                                                            </span>
                                                            <span className="text-xs text-slate-400 md:hidden">
                                                                {u.email}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="text-slate-500 hidden md:table-cell">{u.email}</td>
                                                <td>
                                                    {u.is_extra && (
                                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-fuchsia-700 bg-fuchsia-100 px-2 py-0.5 rounded-full mr-1" data-testid={`extra-badge-${u.id}`}>
                                                            <Sparkles className="w-3 h-3" /> Extra
                                                        </span>
                                                    )}
                                                    {u.is_paypal && (
                                                        <span className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 bg-sky-100 px-2 py-0.5 rounded-full mr-1" data-testid={`paypal-badge-${u.id}`}>
                                                            <Wallet className="w-3 h-3" /> PayPal
                                                        </span>
                                                    )}
                                                    <span className={`badge ${getRoleBadgeClass(u.role)}`}>
                                                        <RoleIcon className="w-3 h-3 mr-1" />
                                                        {getRoleLabel(u.role)}
                                                    </span>
                                                </td>
                                                {(user?.role === 'admin' || user?.role === 'supervisor') && (
                                                    <td className="text-slate-500">
                                                        {u.role === 'usuario' && u.assigned_to_name ? (
                                                            <span className="text-teal-600 font-medium">
                                                                {u.assigned_to_name}
                                                            </span>
                                                        ) : u.role === 'usuario' ? (
                                                            <span className="text-slate-300 italic">Sin asignar</span>
                                                        ) : (
                                                            <span className="text-slate-300">-</span>
                                                        )}
                                                    </td>
                                                )}
                                                <td className="text-slate-500">
                                                    {formatDate(u.created_at)}
                                                </td>
                                                {(user?.role === 'admin' || user?.role === 'supervisor') && (
                                                    <td>
                                                        {canEdit && (
                                                            <DropdownMenu>
                                                                <DropdownMenuTrigger asChild>
                                                                    <Button
                                                                        variant="ghost"
                                                                        size="sm"
                                                                        className="h-8 w-8 p-0"
                                                                        data-testid={`user-actions-${u.id}`}
                                                                    >
                                                                        <MoreVertical className="w-4 h-4" />
                                                                    </Button>
                                                                </DropdownMenuTrigger>
                                                                <DropdownMenuContent align="end">
                                                                    <DropdownMenuItem
                                                                        onClick={() => openEditDialog(u)}
                                                                        className="text-blue-600"
                                                                        data-testid={`edit-user-${u.id}`}
                                                                    >
                                                                        <Pencil className="w-4 h-4 mr-2" />
                                                                        Editar Nombre
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem
                                                                        onClick={() => openPasswordDialog(u)}
                                                                        className="text-orange-600"
                                                                        data-testid={`change-password-${u.id}`}
                                                                    >
                                                                        <KeyRound className="w-4 h-4 mr-2" />
                                                                        Cambiar Contraseña
                                                                    </DropdownMenuItem>
                                                                    {user?.role === 'admin' && (
                                                                        <DropdownMenuItem
                                                                            onClick={() => openShareLinkDialog(u)}
                                                                            className="text-sky-600"
                                                                            data-testid={`share-link-${u.id}`}
                                                                        >
                                                                            <Link2 className="w-4 h-4 mr-2" />
                                                                            Enlace para Compartir
                                                                        </DropdownMenuItem>
                                                                    )}
                                                                    {canManageRole && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            {u.role === 'usuario' && (
                                                                                <>
                                                                                    <DropdownMenuItem
                                                                                        onClick={() => openAssignDialog(u)}
                                                                                        className="text-teal-600"
                                                                                        data-testid={`assign-user-${u.id}`}
                                                                                    >
                                                                                        <UserCheck className="w-4 h-4 mr-2" />
                                                                                        Asignar a Supervisor 2
                                                                                    </DropdownMenuItem>
                                                                                    <DropdownMenuSeparator />
                                                                                    {/* Only admin can promote to full supervisor */}
                                                                                    {user?.role === 'admin' && (
                                                                                        <DropdownMenuItem
                                                                                            onClick={() => handleChangeRole(u.id, 'supervisor')}
                                                                                            className="text-purple-600"
                                                                                            data-testid={`promote-supervisor-${u.id}`}
                                                                                        >
                                                                                            <ArrowUpCircle className="w-4 h-4 mr-2" />
                                                                                            Promover a Supervisor
                                                                                        </DropdownMenuItem>
                                                                                    )}
                                                                                    <DropdownMenuItem
                                                                                        onClick={() => handleChangeRole(u.id, 'supervisor2')}
                                                                                        className="text-amber-600"
                                                                                        data-testid={`promote-supervisor2-${u.id}`}
                                                                                    >
                                                                                        <ArrowUpCircle className="w-4 h-4 mr-2" />
                                                                                        Promover a Supervisor 2
                                                                                    </DropdownMenuItem>
                                                                                </>
                                                                            )}
                                                                            {u.role === 'supervisor2' && (
                                                                                <DropdownMenuItem
                                                                                    onClick={() => handleChangeRole(u.id, 'usuario')}
                                                                                    className="text-slate-600"
                                                                                    data-testid={`demote-user-${u.id}`}
                                                                                >
                                                                                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                                                                                    Degradar a Usuario
                                                                                </DropdownMenuItem>
                                                                            )}
                                                                            {/* Only admin can demote supervisors */}
                                                                            {u.role === 'supervisor' && user?.role === 'admin' && (
                                                                                <DropdownMenuItem
                                                                                    onClick={() => handleChangeRole(u.id, 'usuario')}
                                                                                    className="text-slate-600"
                                                                                    data-testid={`demote-user-${u.id}`}
                                                                                >
                                                                                    <ArrowDownCircle className="w-4 h-4 mr-2" />
                                                                                    Degradar a Usuario
                                                                                </DropdownMenuItem>
                                                                            )}
                                                                        </>
                                                                    )}
                                                                    {user?.role === 'admin' && u.role !== 'admin' && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onClick={() => handleToggleExtra(u.id, !u.is_extra)}
                                                                                className="text-fuchsia-700"
                                                                                data-testid={`toggle-extra-${u.id}`}
                                                                            >
                                                                                <Sparkles className="w-4 h-4 mr-2" />
                                                                                {u.is_extra ? 'Quitar usuario extra' : 'Marcar usuario extra'}
                                                                            </DropdownMenuItem>
                                                                            <DropdownMenuItem
                                                                                onClick={() => handleTogglePayPal(u.id, !u.is_paypal)}
                                                                                className="text-sky-700"
                                                                                data-testid={`toggle-paypal-${u.id}`}
                                                                            >
                                                                                <Wallet className="w-4 h-4 mr-2" />
                                                                                {u.is_paypal ? 'Quitar usuario PayPal' : 'Promover a usuario PayPal'}
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                    {canDelete && (
                                                                        <>
                                                                            <DropdownMenuSeparator />
                                                                            <DropdownMenuItem
                                                                                onClick={() => handleDelete(u.id)}
                                                                                className="text-red-600"
                                                                                data-testid={`delete-user-${u.id}`}
                                                                            >
                                                                                <Trash2 className="w-4 h-4 mr-2" />
                                                                                Eliminar Usuario
                                                                            </DropdownMenuItem>
                                                                        </>
                                                                    )}
                                                                </DropdownMenuContent>
                                                            </DropdownMenu>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="empty-state py-16">
                            <UsersIcon className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                            <p className="text-lg font-medium text-slate-600">No hay usuarios</p>
                            <p className="text-slate-400 mt-1">
                                Crea el primer usuario usando el botón superior
                            </p>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Edit Name Dialog */}
            <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-slate-800">
                            Editar Nombre de Usuario
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5 mt-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name" className="text-slate-700 font-medium">
                                Nuevo Nombre
                            </Label>
                            <div className="relative">
                                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <Input
                                    id="edit-name"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    className="pl-10 h-11"
                                    placeholder="Ingrese el nuevo nombre"
                                    data-testid="edit-name-input"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsEditDialogOpen(false);
                                    setEditingUser(null);
                                    setNewName('');
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleUpdateName}
                                className="flex-1 bg-sky-500 hover:bg-sky-600"
                                data-testid="save-name-btn"
                            >
                                <Pencil className="w-4 h-4 mr-2" />
                                Guardar
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Assign to Supervisor 2 Dialog */}
            <Dialog open={isAssignDialogOpen} onOpenChange={setIsAssignDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-slate-800">
                            Asignar Usuario a Supervisor 2
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5 mt-4">
                        {assigningUser && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                                <p className="text-sm text-slate-600">Usuario seleccionado:</p>
                                <p className="font-semibold text-slate-800">{assigningUser.name}</p>
                                <p className="text-sm text-slate-500">{assigningUser.email}</p>
                                {assigningUser.assigned_to_name && (
                                    <p className="text-sm text-teal-600 mt-1">
                                        Actualmente asignado a: {assigningUser.assigned_to_name}
                                    </p>
                                )}
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label className="text-slate-700 font-medium">
                                Seleccionar Supervisor 2
                            </Label>
                            <Select value={selectedSupervisor2 || 'none'} onValueChange={(val) => setSelectedSupervisor2(val === 'none' ? '' : val)}>
                                <SelectTrigger className="h-11" data-testid="supervisor2-select">
                                    <SelectValue placeholder="Selecciona un Supervisor 2" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">Sin asignar</SelectItem>
                                    {supervisors2.map((sup) => (
                                        <SelectItem key={sup.id} value={sup.id}>
                                            {sup.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {supervisors2.length === 0 && (
                                <p className="text-sm text-amber-600">
                                    No hay Supervisores 2 disponibles. Cree uno primero.
                                </p>
                            )}
                        </div>
                        
                        <div className="flex gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsAssignDialogOpen(false);
                                    setAssigningUser(null);
                                    setSelectedSupervisor2('');
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleAssignToSupervisor2}
                                className="flex-1 bg-teal-500 hover:bg-teal-600"
                                data-testid="save-assign-btn"
                            >
                                <UserCheck className="w-4 h-4 mr-2" />
                                {selectedSupervisor2 ? 'Asignar' : 'Quitar Asignación'}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog open={isPasswordDialogOpen} onOpenChange={setIsPasswordDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-slate-800 flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-orange-500" />
                            Cambiar Contraseña
                        </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-5 mt-4">
                        {passwordUser && (
                            <div className="bg-slate-50 p-3 rounded-lg">
                                <p className="text-sm text-slate-600">Cambiar contraseña de:</p>
                                <p className="font-semibold text-slate-800">{passwordUser.name}</p>
                                <p className="text-sm text-slate-500">{passwordUser.email}</p>
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <Label htmlFor="new-password" className="text-slate-700 font-medium">
                                Nueva Contraseña
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    id="new-password"
                                    type="password"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    className="pl-10 h-11"
                                    placeholder="Mínimo 6 caracteres"
                                    data-testid="new-password-input"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="confirm-password" className="text-slate-700 font-medium">
                                Confirmar Contraseña
                            </Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                                <Input
                                    id="confirm-password"
                                    type="password"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    className="pl-10 h-11"
                                    placeholder="Repita la contraseña"
                                    data-testid="confirm-password-input"
                                />
                            </div>
                        </div>
                        
                        <div className="flex gap-4 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                className="flex-1"
                                onClick={() => {
                                    setIsPasswordDialogOpen(false);
                                    setPasswordUser(null);
                                    setNewPassword('');
                                    setConfirmPassword('');
                                }}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleChangePassword}
                                className="flex-1 bg-orange-500 hover:bg-orange-600"
                                disabled={isSubmitting}
                                data-testid="save-password-btn"
                            >
                                {isSubmitting ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <KeyRound className="w-4 h-4 mr-2" />
                                )}
                                Guardar Contraseña
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Share Link Dialog */}
            <Dialog open={isShareLinkDialogOpen} onOpenChange={setIsShareLinkDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="share-link-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2 text-slate-800">
                            <Link2 className="w-5 h-5 text-sky-500" />
                            Enlace para Compartir
                        </DialogTitle>
                    </DialogHeader>
                    {shareLinkUser && (
                        <div className="space-y-4">
                            <p className="text-sm text-slate-500">
                                Asignar enlace para <span className="font-medium text-slate-700">{shareLinkUser.name}</span>
                            </p>
                            <input
                                type="url"
                                placeholder="https://ejemplo.com/enlace"
                                value={shareLinkValue}
                                onChange={(e) => setShareLinkValue(e.target.value)}
                                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-300"
                                data-testid="share-link-input"
                            />
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    className="flex-1"
                                    onClick={() => setIsShareLinkDialogOpen(false)}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    className="flex-1 bg-sky-500 hover:bg-sky-600"
                                    onClick={handleSaveShareLink}
                                    disabled={isSubmitting}
                                    data-testid="save-share-link-btn"
                                >
                                    {isSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Link2 className="w-4 h-4 mr-2" />}
                                    Guardar
                                </Button>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
